import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { useConfirm } from "../context/ConfirmContext";
import { AsyncButton } from "../components/AsyncButton";
import { formatDate } from "../utils/dateFormat";
import { useTenantTimezone } from "../hooks/useTenantTimezone";
import { LoadingOverlay } from "../components/LoadingOverlay";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:30801";
const NAVY = "#1e3a8a";
const TEAL = "#00c9a7";

const ALL_CATEGORIES  = ["All", "HR", "IT", "Finance", "Legal", "Operations", "General", "Other"];
const FORM_CATEGORIES = ["HR", "IT", "Finance", "Legal", "Operations", "General", "Other"];

interface Policy {
  id: number;
  title: string;
  category: string;
  description: string | null;
  file_name: string;
  file_type: string;
  has_text: boolean;
  created_at: string;
}

const FILE_ICON: Record<string, string> = { pdf: "📄", docx: "📝", txt: "📃" };

export default function PoliciesPage() {
  const { user } = useAuth();
  const tz = useTenantTimezone();
  const confirm = useConfirm();
  const canManage = ["admin", "admin_assistant", "hr"].includes(user?.role || "");

  const [policies,   setPolicies]   = useState<Policy[]>([]);
  const [filterCat,  setFilterCat]  = useState("All");
  const [loading,    setLoading]    = useState(true);

  // Upload form
  const [showUpload,   setShowUpload]   = useState(false);
  const [upTitle,      setUpTitle]      = useState("");
  const [upCategory,   setUpCategory]   = useState("HR");
  const [upDescription,setUpDescription]= useState("");
  const [upFile,       setUpFile]       = useState<File | null>(null);
  const [uploading,    setUploading]    = useState(false);
  const [upError,      setUpError]      = useState("");
  const [reextracting, setReextracting] = useState<number | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function fetchPolicies() {
    try {
      const r = await fetch(`${API_URL}/api/policies`, { credentials: "include" });
      if (r.ok) setPolicies(await r.json());
    } finally { setLoading(false); }
  }

  useEffect(() => { fetchPolicies(); }, []);

  async function handleUpload() {
    if (!upTitle.trim() || !upFile) return;
    setUploading(true); setUpError("");
    try {
      const fd = new FormData();
      fd.append("title",       upTitle.trim());
      fd.append("category",    upCategory);
      fd.append("description", upDescription.trim());
      fd.append("file",        upFile);
      const r = await fetch(`${API_URL}/api/policies`, { method: "POST", credentials: "include", body: fd });
      const d = await r.json();
      if (!r.ok) { setUpError(d.detail || "Upload failed"); return; }
      setShowUpload(false);
      setUpTitle(""); setUpCategory("HR"); setUpDescription(""); setUpFile(null);
      fetchPolicies();
    } catch { setUpError("Network error. Please try again."); }
    finally { setUploading(false); }
  }

  async function handleReextract(id: number) {
    setReextracting(id);
    try {
      const r = await fetch(`${API_URL}/api/policies/${id}/reextract`, { method: "POST", credentials: "include" });
      const d = await r.json();
      if (r.ok) {
        fetchPolicies();
        alert(d.extracted ? `Text extracted (${d.chars} chars). AI can now search this document.` : "Still no text found — the file may be a pure image scan with no readable text.");
      }
    } finally { setReextracting(null); }
  }

  async function handleDelete(id: number, title: string) {
    if (!await confirm(`Remove "${title}"? Employees will no longer be able to view this policy.`,
      { title: "Remove policy", confirmLabel: "Remove" })) return;
    await fetch(`${API_URL}/api/policies/${id}`, { method: "DELETE", credentials: "include" });
    fetchPolicies();
  }

  const displayed = filterCat === "All" ? policies : policies.filter(p => p.category === filterCat);

  const inp: React.CSSProperties = {
    width: "100%", padding: "9px 12px", borderRadius: 8,
    border: "1.5px solid #e2e8f0", fontSize: 14, outline: "none",
    boxSizing: "border-box", fontFamily: "inherit",
  };

  return (
    <div>
      {/* Width and padding come from ShellMain. This is a list page like
          Candidates, Jobs, Offers and Talent Pool — all full width — so a
          cap here left it visibly narrower than its siblings, and its own
          padding stacked on the shell's. */}
      <LoadingOverlay active={uploading || reextracting !== null} />

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: "#0f172a", margin: 0 }}>Company Policies</h1>
          <p style={{ color: "#64748b", fontSize: 14, margin: "4px 0 0" }}>
            Download policy documents · Ask the AI chatbot any policy question
          </p>
        </div>
        {canManage && (
          <button
            onClick={() => { setShowUpload(v => !v); setUpError(""); }}
            style={{
              background: showUpload ? "#f1f5f9" : `linear-gradient(135deg, ${NAVY}, ${TEAL})`,
              color: showUpload ? "#64748b" : "white",
              border: showUpload ? "1.5px solid #e2e8f0" : "none",
              borderRadius: 9, padding: "10px 20px", fontSize: 14, fontWeight: 700, cursor: "pointer",
            }}
          >
            {showUpload ? "✕ Cancel" : "+ Upload Policy"}
          </button>
        )}
      </div>

      {/* ── Upload form ─────────────────────────────────────────────────── */}
      {showUpload && canManage && (
        <div style={{
          background: "white", borderRadius: 14, padding: "24px 28px",
          border: "1.5px solid #dbeafe", marginBottom: 28,
          boxShadow: "0 2px 12px rgba(30,58,138,0.06)",
        }}>
          <h3 style={{ margin: "0 0 20px", fontSize: 16, fontWeight: 700, color: NAVY }}>Upload New Policy</h3>

          {upError && (
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13, color: "#dc2626" }}>
              {upError}
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>Title *</label>
              <input style={inp} value={upTitle} onChange={e => setUpTitle(e.target.value)} placeholder="e.g. Leave Policy 2024" autoFocus />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>Category</label>
              <select style={{ ...inp, background: "white" }} value={upCategory} onChange={e => setUpCategory(e.target.value)}>
                {FORM_CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>Description (optional)</label>
            <textarea
              style={{ ...inp, resize: "vertical" as const }} rows={2}
              value={upDescription} onChange={e => setUpDescription(e.target.value)}
              placeholder="Brief summary of this policy…"
            />
          </div>

          <div style={{ marginBottom: 22 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>
              File * <span style={{ fontWeight: 400, color: "#94a3b8" }}>(PDF, DOCX or TXT · max 10 MB)</span>
            </label>
            <div
              onClick={() => fileRef.current?.click()}
              style={{
                border: `2px dashed ${upFile ? "#86efac" : "#e2e8f0"}`,
                borderRadius: 10, padding: "22px 16px", textAlign: "center",
                cursor: "pointer", background: upFile ? "#f0fdf4" : "#fafafa",
                transition: "all 0.15s",
              }}
            >
              <input ref={fileRef} type="file" accept=".pdf,.docx,.txt" style={{ display: "none" }}
                onChange={e => setUpFile(e.target.files?.[0] || null)} />
              {upFile ? (
                <span style={{ color: "#16a34a", fontWeight: 600, fontSize: 14 }}>
                  {FILE_ICON[upFile.name.split(".").pop()?.toLowerCase() || ""] || "📎"} {upFile.name}
                </span>
              ) : (
                <span style={{ color: "#94a3b8", fontSize: 13 }}>Click to choose a file</span>
              )}
            </div>
          </div>

          <button
            onClick={handleUpload}
            disabled={!upTitle.trim() || !upFile || uploading}
            style={{
              background: !upTitle.trim() || !upFile || uploading
                ? "#94a3b8"
                : `linear-gradient(135deg, ${NAVY}, ${TEAL})`,
              color: "white", border: "none", borderRadius: 9,
              padding: "11px 28px", fontSize: 14, fontWeight: 700,
              cursor: !upTitle.trim() || !upFile || uploading ? "not-allowed" : "pointer",
            }}
          >
            {uploading ? "Uploading…" : "Upload Policy"}
          </button>
        </div>
      )}

      {/* ── Category filter ─────────────────────────────────────────────── */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 22 }}>
        {ALL_CATEGORIES.map(cat => (
          <button
            key={cat}
            onClick={() => setFilterCat(cat)}
            style={{
              padding: "5px 14px", borderRadius: 20,
              border: `1.5px solid ${filterCat === cat ? NAVY : "#e2e8f0"}`,
              background: filterCat === cat ? NAVY : "white",
              color: filterCat === cat ? "white" : "#475569",
              fontSize: 13, fontWeight: 600, cursor: "pointer",
            }}
          >{cat}</button>
        ))}
      </div>

      {/* ── Policy cards ────────────────────────────────────────────────── */}
      {loading ? (
        <p style={{ color: "#94a3b8", textAlign: "center", padding: 48 }}>Loading…</p>
      ) : displayed.length === 0 ? (
        <div style={{ textAlign: "center", padding: "64px 24px", color: "#94a3b8" }}>
          <div style={{ fontSize: 44, marginBottom: 12 }}>📋</div>
          <p style={{ fontSize: 15, margin: 0 }}>
            {filterCat === "All" ? "No policies uploaded yet." : `No ${filterCat} policies yet.`}
          </p>
          {canManage && filterCat === "All" && (
            <p style={{ fontSize: 13, marginTop: 6 }}>Click "Upload Policy" to add the first one.</p>
          )}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(270px, 1fr))", gap: 16 }}>
          {displayed.map(pol => (
            <div key={pol.id} style={{
              background: "white", borderRadius: 14, padding: "20px 22px",
              border: "1.5px solid #e2e8f0",
              boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
              display: "flex", flexDirection: "column", gap: 10,
            }}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 6 }}>
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: "2px 9px", borderRadius: 20,
                      background: "#eff6ff", color: NAVY, letterSpacing: 0.4,
                    }}>{pol.category || "General"}</span>
                    <span style={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase" as const }}>{pol.file_type}</span>
                  </div>
                  <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#0f172a", lineHeight: 1.4 }}>{pol.title}</h3>
                </div>
                <span style={{ fontSize: 26, flexShrink: 0 }}>{FILE_ICON[pol.file_type] || "📎"}</span>
              </div>

              {pol.description && (
                <p style={{ margin: 0, fontSize: 13, color: "#64748b", lineHeight: 1.6 }}>{pol.description}</p>
              )}

              <p
                style={{
                  margin: 0, fontSize: 11, color: "#94a3b8",
                  display: "flex", alignItems: "center", gap: 4, minWidth: 0,
                }}
              >
                <span
                  title={pol.file_name}
                  style={{
                    overflow: "hidden", textOverflow: "ellipsis",
                    whiteSpace: "nowrap", minWidth: 0,
                  }}
                >{pol.file_name}</span>
                <span style={{ flexShrink: 0 }}>· {formatDate(pol.created_at, "short", tz)}</span>
              </p>

              {!pol.has_text && (
                <div style={{ fontSize: 11, color: "#b45309", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "4px 8px" }}>
                  ⚠ No text extracted — AI cannot search this document
                </div>
              )}

              <div style={{ display: "flex", gap: 8, marginTop: "auto", flexWrap: "wrap" }}>
                <a
                  href={`${API_URL}/api/policies/${pol.id}/download`}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    flex: 1, padding: "8px 0", borderRadius: 8, textAlign: "center",
                    background: `linear-gradient(135deg, ${NAVY}, ${TEAL})`,
                    color: "white", textDecoration: "none", fontSize: 13, fontWeight: 600,
                    minWidth: 80,
                  }}
                >
                  ↓ Download
                </a>
                {canManage && !pol.has_text && (
                  <button
                    onClick={() => handleReextract(pol.id)}
                    disabled={reextracting === pol.id}
                    style={{
                      padding: "8px 10px", borderRadius: 8,
                      background: "#eff6ff", color: NAVY,
                      border: `1px solid #bfdbfe`, fontSize: 12, fontWeight: 600,
                      cursor: reextracting === pol.id ? "wait" : "pointer",
                    }}
                  >{reextracting === pol.id ? "…" : "Re-extract"}</button>
                )}
                {canManage && (
                  <AsyncButton
                    onClick={() => handleDelete(pol.id, pol.title)}
                    style={{
                      padding: "8px 14px", borderRadius: 8,
                      background: "#fef2f2", color: "#dc2626",
                      border: "1px solid #fecaca", fontSize: 13, fontWeight: 600, cursor: "pointer",
                    }}
                  >Delete</AsyncButton>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
